import {
  STATE_ABBREVIATION_NAME_MAP,
  STATE_NAME_ABBREVIATION_MAP,
} from '#shared';

import { EFocusView } from './focusView.enum';

const states = Object.keys(
  STATE_NAME_ABBREVIATION_MAP,
) as (keyof typeof STATE_NAME_ABBREVIATION_MAP)[];

const nonContinentalUsStates = [
  STATE_ABBREVIATION_NAME_MAP.AK,
  STATE_ABBREVIATION_NAME_MAP.HI,
  STATE_ABBREVIATION_NAME_MAP.PR,
];

// ---------------------------------------------------------------------------
// Europe
//
// Country names below must match the `name` property in /data/countries.geojson
// (Natural Earth). A couple differ from the common short name (noted inline).
// ---------------------------------------------------------------------------

// All European countries we highlight on the map with the same styling as Italy.
// Italy itself is highlighted via FOCUS_VIEW.Italy / TMDX_SERVED_AREAS, so it is
// intentionally not repeated here.
export const EUROPE_HIGHLIGHTED_AREAS = [
  'Albania',
  'Andorra',
  'Austria',
  'Belgium',
  'Bosnia and Herzegovina',
  'Bulgaria',
  'Croatia',
  'Czechia', // "Czech Republic"
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Iceland',
  'Ireland',
  'Kosovo',
  'Latvia',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Moldova',
  'Monaco',
  'Montenegro',
  'Netherlands',
  'North Macedonia',
  'Norway',
  'Poland',
  'Portugal',
  'Romania',
  'San Marino',
  'Republic of Serbia', // "Serbia"
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'Switzerland',
  'Turkey',
  'Ukraine',
  'United Kingdom',
];

// Europe camera-framing preset for the Focus View widget. This intentionally
// excludes Scandinavia, the British Isles and the northern Baltic so the
// camera frames a southern band roughly from Iberia/Italy up to the Baltic
// Sea. Highlighting is driven by TMDX_SERVED_AREAS, so countries dropped
// here still appear highlighted on the map.
const EUROPE_FOCUS_AREAS = [
  'Albania',
  'Andorra',
  'Austria',
  'Belgium',
  'Bosnia and Herzegovina',
  'Bulgaria',
  'Croatia',
  'Czechia',
  'Denmark',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Italy',
  'Kosovo',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Moldova',
  'Monaco',
  'Montenegro',
  'Netherlands',
  'North Macedonia',
  'Poland',
  'Portugal',
  'Romania',
  'San Marino',
  'Republic of Serbia',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Switzerland',
  'Turkey',
];

export const FOCUS_VIEW: {
  [K in EFocusView]: K extends `USA ${string}`
    ? (keyof typeof STATE_NAME_ABBREVIATION_MAP)[]
    : string[];
} = {
  [EFocusView.US_CONTINENTAL]: states.filter(
    s => !nonContinentalUsStates.includes(s),
  ),
  [EFocusView.US_PUERTO_RICO]: ['Puerto Rico'],
  [EFocusView.US_ALASKA]: ['Alaska'],
  [EFocusView.US_HAWAII]: ['Hawaii'],
  [EFocusView.US_SOUTH]: ['Arkansas', 'Louisiana', 'Oklahoma', 'Texas'],
  [EFocusView.US_SOUTHEAST]: [
    'Delaware',
    'Florida',
    'Georgia',
    'Maryland',
    'North Carolina',
    'South Carolina',
    'Virginia',
    'West Virginia',
    'Alabama',
    'Kentucky',
    'Mississippi',
    'Tennessee',
  ],
  [EFocusView.US_SOUTHWEST]: [
    'Arizona',
    'Colorado',
    'Nevada',
    'New Mexico',
    'Utah',
    'California',
  ],
  [EFocusView.US_WEST]: ['Washington', 'Idaho', 'Wyoming', 'Montana'],
  [EFocusView.US_MIDWEST]: [
    'Indiana',
    'Illinois',
    'Michigan',
    'Ohio',
    'Wisconsin',
    'Iowa',
    'Kansas',
    'Minnesota',
    'Missouri',
    'Nebraska',
    'North Dakota',
    'South Dakota',
  ],
  [EFocusView.US_NORTHEAST]: [
    'Connecticut',
    'Maine',
    'Massachusetts',
    'New Hampshire',
    'Rhode Island',
    'Vermont',
    'New Jersey',
    'New York',
    'Pennsylvania',
  ],
  [EFocusView.EU_ITALY]: ['Italy'],
  [EFocusView.MIDDLE_EAST]: [
    'United Arab Emirates',
    'Bahrain',
    'Kuwait',
    'Saudi Arabia',
    'Oman',
    'Qatar',
  ],
  [EFocusView.EUROPE]: EUROPE_FOCUS_AREAS,
};

export const ITALIAN_HUBS = ['MLN', 'BOL', 'ROM', 'BAR'] as const;
export const DUTCH_HUBS = ['UTR'] as const;

export const EUROPEAN_HUBS = [...ITALIAN_HUBS, ...DUTCH_HUBS] as const;

export const TMDX_SERVED_AREAS = [
  ...FOCUS_VIEW.Italy,
  ...FOCUS_VIEW['Middle East'],
  ...EUROPE_HIGHLIGHTED_AREAS,
] as const;

export const EUROPEAN_FOCUS_VIEWS: Partial<EFocusView[]> = [
  EFocusView.EUROPE,
  EFocusView.EU_ITALY,
] as const;

// On-map labels for highlighted countries. Defaults to the ISO 3166-1 alpha-3
// code from countries.geojson; this overrides entries that are missing a code
// there (stored as "-99") or that should read more naturally.
export const COUNTRY_LABEL_OVERRIDES: Record<string, string> = {
  'United Arab Emirates': 'UAE',
  France: 'FRA',
  Norway: 'NOR',
  Kosovo: 'XKX',
};
